import { useCallback, useLayoutEffect } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Select,
  type SelectOption,
  Text,
} from '@enechain/ecloud-designsystem'

import {
  DS_SELECT_CLASS_PREFIX,
  ensureDsSelectNonInteractiveStyles,
} from '~/components/common/dsSelectMenuStyles'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { useWidestOptionMinWidth } from '~/hooks/useWidestOptionMinWidth'

/** 単一選択 select の選択肢 1 件。 value は必ず string (number は呼び出し側で `String()` する)。 */
export type DsSelectOption = {
  value: string
  label: string
  /**
   * 選べない選択肢 (native select の `<option disabled>` 相当)。
   * menu には残すが選択させない用途 (例: 需要想定の気象地点切替候補で利用不可の地点)。
   */
  disabled?: boolean
  /** 無効な選択肢の名前の下に添える理由 (action-reason.md)。 */
  disabledReason?: string
}

/**
 * 入れ子の選択肢 (= native select の `<optgroup>` 相当)。 `label` が見出しになる。
 * `label` が空のグループは見出しを出さず、 区切り線で前の選択肢と分ける
 * (見出しの要らない別区分。 native select の `<hr>` 相当)。
 */
export type DsSelectOptionGroup = {
  label: string
  options: readonly DsSelectOption[]
}

const formatOptionLabel = (
  option: SelectOption,
  { context }: { context: 'menu' | 'value' },
): React.ReactNode => {
  const item = option as DsSelectOption
  if (context !== 'menu' || !item.disabled || !item.disabledReason) {
    return item.label
  }
  return (
    <Box>
      {item.label}
      <Text textStyle="sm" color="Text.midEmphasis">
        {item.disabledReason}
      </Text>
    </Box>
  )
}

/** フラット / グループ混在の選択肢リスト。 同一配列に両方を混ぜてよい。 */
export type DsSelectOptions = ReadonlyArray<
  DsSelectOption | DsSelectOptionGroup
>

const isOptionGroup = (
  option: DsSelectOption | DsSelectOptionGroup,
): option is DsSelectOptionGroup => 'options' in option

/**
 * グループを展開して option だけの配列にする。
 * 「選択中の value に対応する option を引く」用途と、 呼び出し側の件数判定に使う。
 */
export const flattenDsSelectOptions = (
  options: DsSelectOptions,
): DsSelectOption[] =>
  options.flatMap((option) =>
    isOptionGroup(option) ? [...option.options] : [option],
  )

/**
 * 選択中の `value` に対応する option を決める。
 * options に無い場合は `fallbackLabel` の文言で「表示だけの option」を作る
 * (作れなければ `null` = placeholder 表示)。
 */
const resolveSelectedOption = (
  options: DsSelectOptions,
  currentValue: string | undefined,
  fallbackLabel: ((value: string) => string | undefined) | undefined,
): DsSelectOption | null => {
  if (currentValue === undefined) return null
  const matched = flattenDsSelectOptions(options).find(
    (o) => o.value === currentValue,
  )
  if (matched !== undefined) return matched
  const label = fallbackLabel?.(currentValue)
  return label === undefined ? null : { value: currentValue, label }
}

type Props = {
  /** 選択中の値。 number を渡した場合は `String()` して option と突き合わせる。 */
  value: string | number | null | undefined
  /** 選択が変わった時に呼ばれる。 clear 時は `undefined`。 */
  onChange: (value: string | undefined) => void
  options: DsSelectOptions
  /**
   * 未選択時の文言。 既定は `common:form.selectPlaceholder` (「選択」。 frontend rules §3b /
   * UI 辞書 components/select.md)。 フィルタ (空選択 = 全件) は「すべて」 を渡す。
   */
  placeholder?: string
  'aria-label'?: string
  'aria-describedby'?: string
  isDisabled?: boolean
  /**
   * **表示中と同じ選択肢を選び直した時にも `onChange` を発火させる。**
   *
   * 既定 (`false`) は native `<select>` 相当で、 同値の選び直しは握り潰す
   * (JSDoc「native select との差分吸収」 参照)。 だが「今の表示値は**導出**であって
   * ユーザーが**明示的に選んだ**わけではない」 という区別を持つ画面では、 同じ値の
   * 選び直しが「導出 → 明示」 の確定操作として意味を持つ。 その場合だけ true にする。
   *
   * 実例 (承認フロー): セルが混在していると一括セレクトは**導出**で「個別設定」 を
   * 表示する。 ここでユーザーが「個別設定」 を選ぶのは「明示的な個別設定に入る」
   * 操作で、 行/列の一括値がリセットされ各セルが編集可能になる (AC-APF-011c)。
   * 同値ガードを素通しすると**この操作が丸ごと無反応 (dead click) になる**。
   */
  emitOnReselect?: boolean
  /**
   * エラー状態 (DS `Select` の `invalid`)。 赤枠 + `aria-invalid` が付く。
   * `FieldRoot invalid` 配下では field context から自動で入るので、
   * field を持たない画面 (raw table のセル等) で明示する時だけ渡す。
   */
  invalid?: boolean
  onBlur?: () => void
  /**
   * `value` が `options` に無い時に、 閉じた状態へ表示するラベルを作る。
   * `undefined` を返すと placeholder 表示に戻る。 詳細は下の JSDoc「既存値のフォールバック」。
   */
  fallbackLabel?: (value: string) => string | undefined
  /**
   * 内側 input の `id`。 外部の `<label htmlFor>` と関連付ける時に渡す
   * (DS `Select` は `id` を受けず、 react-select の `inputId` が本体)。
   * 省略時は field context の id が使われる (= `FieldRoot` + `FieldLabel` の通常形)。
   */
  inputId?: string
  /**
   * 内側 input への参照を返す。 React Hook Form の `shouldFocusError`
   * (submit 失敗時に最初のエラー項目へ focus/scroll) を成立させるために使う。
   * DS `Select` は `ref` を外側の `Box` に流すだけで input には届かないため、
   * ここで DOM を辿って渡す。 呼び出しは mount / unmount 時のみ。
   */
  inputRef?: (el: HTMLInputElement | null) => void
  /**
   * 幅は wrapper の `Box` に当てる (flex 配下で潰れる / 伸びるのを呼び出し側が制御するため)。
   * どれも渡さない時は、 内容の幅で決まる select に限り一番長い選択肢が入る幅を確保する
   * (選び替えで幅が変わらない。 UI 辞書 components/select.md・frontend rules §3b)。
   */
  width?: string
  minWidth?: string
  maxWidth?: string
  /**
   * flex 配下で**縮ませない**時に `0` を渡す。
   *
   * `width="auto"` (内容に合わせる) だけでは足りない: flex item の既定は
   * `flex-shrink: 1` なので、行が詰まると幅が削られて選択中の値が省略される。
   * 「選択値を省略させない」 は `width` ではなく**この 2 つの組**で決まる。
   */
  flexShrink?: number
}

/**
 * DS `Select` (単一選択) を **value / onChange の制御コンポーネント**として使うための共通部品。
 *
 * 見た目 (font 補正) と modal 内の portal 対策を**ここ 1 箇所**に集約する。
 * React Hook Form 配下では本部品を直接使わず [[FormSelect]] を使う
 * (= `Controller` の配線も 1 箇所に閉じ込めるため)。
 *
 * ## なぜ native select ではなく DS Select か
 *
 * 単一 select の正は **DS の `Select`** (2026-08-05 デザイナー確定)。
 *
 * **[design 差異]** prototype (8080) はフィルタ・モーダルとも native select
 * (`.ecl-select`) を使う。本部品は prototype ではなく **DS 部品への統一**を選ぶ
 * デザイナー判断に基づく。
 *
 * ## native select との差分吸収
 *
 * 呼び出し側が native `<select>` と同じ前提で書けるよう、 react-select との
 * 差分は**本部品が吸収する**。
 *
 * - `<option disabled>` 相当 → `DsSelectOption.disabled` (`isOptionDisabled` へ橋渡し)。
 *   見た目はグループ見出しと同じく太字 + `Text.midEmphasis`
 *   (`dsSelectNonInteractiveItemStyle`)。 DS に混在リストの recipe は無い。
 * - **同じ選択肢を選び直した時は `onChange` を発火させない** — native `<select>` は
 *   値が変わらない選択で change を出さないが、 react-select は単一選択でも同値
 *   ガードを持たず毎回 `onChange` を呼ぶ。 素通しすると「値は同じなのに変更あり」と
 *   誤判定され、 dirty 管理を持つ画面 (承認フロー) で保存ボタンが活性化し離脱
 *   ガードまで出た。 判定は正規化後の `value` (number は `String()` 済み) との比較。
 *   ただし**「表示値が導出で、 同値の選び直しが確定操作になる」 select は例外**で、
 *   `emitOnReselect` で opt-out する (同 prop の JSDoc 参照)。
 *
 * ## menu の portal 先 (modal 内で「選択肢が押せない」を防ぐ)
 *
 * DS `Modal` (= ark Dialog) は開いている間 `<body>` に **`inert` + `pointer-events: none`**
 * を付ける (2026-08-06 実測: `<body data-inert="" style="pointer-events: none">`)。
 * このため menu を `document.body` へ portal すると **menu 全体が操作不能領域に入り、
 * 選択肢をクリックしても反応しない**。
 *
 * 一方で portal 先を dialog content に変えると、 chakra の dialog content が
 * transform で containing block を作るため **react-select の `position: fixed` 配置が
 * ズレる** (2026-08-06 実測: menu が modal の外・右下に出た)。
 *
 * そこで **portal 先は body のまま**にし、 menu portal の style に
 * `pointerEvents: 'auto'` を当てて操作可能に戻す。 body に付くのは `inert` 属性ではなく
 * inline の `pointer-events: none` なので、 子側で打ち消せる。
 *
 * **DS `Select` を直書きする時も同じ対策が要る**。 逆に言えば、 直書きせず本部品
 * (または [[FormSelect]]) を使えば対策は自動で付く。
 *
 * ## 既存値のフォールバック (`fallbackLabel`)
 *
 * native select 時代は「選択中の値が候補に無い場合、 呼び出し側で `<option>` を 1 つ
 * 足す」 パターンを各所で書いていた (物理削除済み ID / 有効期間外で候補から外れた ID)。
 * DS `Select` は `selectedOptions` で選択中の option を渡す形なので、 **候補に無い値でも
 * 表示だけできる**。 これを `fallbackLabel` に集約する。
 *
 * ```tsx
 * <DsSelect
 *   value={entry.bgMasterId}
 *   onChange={(v) => { f.onChange(v ?? '') }}
 *   options={bgOptsForRow}
 *   fallbackLabel={(v) => allBgMasters.find((b) => b.id === v)?.bgCode ?? '(削除済み)'}
 * />
 * ```
 *
 * **なぜ「呼び出し側で options に足す」ではなくこの形か**:
 * options に足すと**その値が menu の選択肢としても並ぶ**ため、 「(削除済み)」 のような
 * 選べてはいけない値を再選択できてしまう (native select 時代から残っていた欠陥)。
 * `fallbackLabel` は**閉じた状態の表示だけ**を担い、 menu には出さない。
 * 逆に「候補として選べてよい」 場合は従来どおり呼び出し側で `options` に足せばよい
 * (本部品は `options` を素直に引くので、 その形も無改修で動く)。
 *
 * ## 選択肢のグループ (`<optgroup>` 相当)
 *
 * `options` に `{ label, options }` を混ぜるとグループ見出し付きで描画される。
 * フラットな option とグループを同一配列に混在させてよい (例: 先頭に「BG全体」、
 * その後ろに「小売電気事業者」グループ)。 見出しは選べない行なので
 * `dsSelectNonInteractiveItemStyle` を当てる。
 */
export const DsSelect = ({
  value,
  onChange,
  options,
  placeholder,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  isDisabled,
  emitOnReselect = false,
  invalid,
  onBlur,
  fallbackLabel,
  inputId,
  inputRef,
  width,
  minWidth,
  maxWidth,
  flexShrink,
}: Props): React.ReactElement => {
  // number → string の正規化点。 表示 (selected) と同値ガード (onChange) の
  // 両方がこの 1 つの値を見る。
  const currentValue =
    value === undefined || value === null ? undefined : String(value)
  const selected = resolveSelectedOption(options, currentValue, fallbackLabel)
  const { t } = useTranslation()
  useLayoutEffect(() => {
    ensureDsSelectNonInteractiveStyles()
  }, [])
  // 幅を呼び出し側が決めている時は、 そちらを優先して自動の確保はしない。
  const hasExplicitWidth =
    width !== undefined || minWidth !== undefined || maxWidth !== undefined
  // 確保するのは選択肢の幅 (select.md「最長の選択肢 + 91px」)。 placeholder や options に無い値の
  // ラベルは表示中なら select が自分の幅で収めるので、 ここに足さない。
  const widest = useWidestOptionMinWidth(
    hasExplicitWidth
      ? []
      : flattenDsSelectOptions(options).map((option) => option.label),
  )
  const setWidestRef = widest.ref
  const wrapperRef = useCallback(
    (el: HTMLDivElement | null) => {
      setWidestRef(el)
      // react-select は input を内部で描くので、 wrapper から辿って渡す。
      const input = el?.querySelector('input') ?? null
      if (input && ariaDescribedBy)
        input.setAttribute('aria-describedby', ariaDescribedBy)
      inputRef?.(input)
    },
    [setWidestRef, inputRef, ariaDescribedBy],
  )

  return (
    // 閉じた状態の font-size を design の 14px に揃える。 chakraStyles は使えない
    // (DS が自前の chakraStyles を先に置き `{...rest}` が後勝ちで丸ごと消える)。
    // 理由の詳細は selectFontFix の JSDoc 参照。
    // [配置メモ] selectFontFix は filters/ 配下にあるがフィルタ専用ではない。
    // import 元が複数あり、 移動すると同時期の他 PR と衝突するため据え置く。
    <Box
      ref={wrapperRef}
      flexShrink={flexShrink}
      width={width}
      minWidth={hasExplicitWidth ? minWidth : widest.minWidth}
      maxWidth={maxWidth}
      css={selectFontFix}
    >
      <Select<SelectOption, false>
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        classNamePrefix={DS_SELECT_CLASS_PREFIX}
        formatOptionLabel={formatOptionLabel}
        inputId={inputId}
        invalid={invalid}
        isDisabled={isDisabled}
        isMulti={false}
        // option 単位の無効化 (native select の `<option disabled>` 相当)。
        // react-select は既定で `isDisabled` key しか見ないため明示的に橋渡しする。
        isOptionDisabled={(opt) =>
          (opt as unknown as DsSelectOption).disabled === true
        }
        // menu は body へ portal する (親の overflow に切られないため、 かつ
        // react-select の fixed 配置が containing block の影響を受けないため)。
        menuPlacement="auto"
        /* portal しても **下方向固定のままだと viewport を突き抜ける**。
           react-select の既定は menuPlacement="bottom" で、 空きが足りなくても
           下に出し続けるため、 画面下部の select では後半の option に届かなくなる
           (position:fixed なのでページを送っても menu が一緒に動く)。
           "auto" にすると空きに応じて上下を選び、 高さも viewport に収める。
           自前 dropdown の MultiSelect / design プロトの positionMenu と同じ方針
           (2026-08-27 デザイナー指摘「list 見切れ」 の横展開)。 */
        menuPortalTarget={document.body}
        onBlur={onBlur}
        onChange={(opt) => {
          const nextValue = opt?.value ?? undefined
          // 表示中と同じ値を選び直した時は発火させない (native select 相当)。
          // react-select は単一選択でも同値ガードを持たず、 選び直しのたびに
          // onChange を呼ぶ。 これをそのまま流すと呼び出し側は値が変わっていない
          // のに「変更あり」と判断してしまう (承認フローで no-op の選び直しが
          // dirty 扱いになり、 保存ボタン活性化 + 離脱ガード誤発火)。
          // 詳細は JSDoc「native select との差分吸収」。
          // `emitOnReselect` の画面は「同じ値の選び直し」自体が確定操作なので通す。
          if (!emitOnReselect && nextValue === currentValue) return
          onChange(nextValue)
        }}
        // DS の `SelectProps` は `options: Option[]` と宣言しており、 グループ
        // (`SelectGroup` = react-select の `GroupBase`) を混ぜた配列を型で受けられない。
        // 描画側 (chakra-react-select) は `OptionsOrGroups` を素で解釈できるため、
        // 境界でのみ型を落とす。 公開 API 側 (`DsSelectOptions`) は型安全に保つ。
        options={options as unknown as SelectOption[]}
        placeholder={placeholder ?? t('common:form.selectPlaceholder')}
        selectedOptions={selected}
        // [DS差異] filter / toolbar / 表の中も md。 UI 辞書 components/select.md「`--sm` も同じ寸法」:
        // DS の `sm` (34px / ▼ の左の余白 10px) は同じ行の入力欄・ボタン (40px) や他の select と
        // 高さ・区切り線の位置がずれる。
        size="md"
        styles={{
          menuPortal: (base) => ({
            ...base,
            // zIndex は chakra の popover (1500) 相当 = modal (1400) より前面。
            zIndex: 1500,
            // DS `Modal` (= ark Dialog) は開いている間 `<body>` に
            // `pointer-events: none` を付ける (2026-08-06 実測)。 body へ portal した
            // menu はこれを継承して**クリックできなくなる**ため明示的に戻す。
            // 詳細は JSDoc「menu の portal 先」参照。
            pointerEvents: 'auto',
          }),
        }}
      />
    </Box>
  )
}
